/**
 * S-45 «Архив месяцев» — выгрузка закрывшегося месяца в Excel (заказчик 02.11.2026).
 *
 * Зачем: «в кабинете WB отчёты за три месяца ещё посмотришь, потом концов не найдёшь».
 * Поэтому закрывшийся месяц собирается в файл и хранится у нас — в базе и в облаке.
 * Внутри пять листов: сводка, по моделям, по дням, детализация «день × модель» и сырые
 * строки отчётов WB о реализации — как их отдал сам WB.
 *
 * Архив собирается сам, как только месяц закрыт отчётами. Кнопки здесь — чтобы забрать файл
 * и чтобы собрать месяц, до которого у системы ещё не дошли руки.
 */
import { useState } from 'react'
import { apiFile } from '../api/client.js'
import { buildArchive, fetchArchives, sendArchive, type ArchiveItem, type ArchivesSlice } from '../api/sections.js'
import { Failed, Loading, NoShop, useData, useScreenHead } from './common.js'
import { Sect } from './ui.js'

const NBSP = String.fromCharCode(160)
const MINUS = '−'
const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

const rub = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—'
  const r = Math.round(n)
  return (r < 0 ? MINUS : '') + Math.abs(r).toLocaleString('ru-RU') + NBSP + '₽'
}
const mb = (bytes: number): string => (bytes / 1024 / 1024).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + NBSP + 'МБ'

function monthTitle(month: string): string {
  const [y = '', m = ''] = month.split('-')
  return `${MONTHS[Number(m) - 1] ?? month} ${y}`
}

/** «2 ноября, 16:40» — когда файл собран. */
function builtAt(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
}

export function ScreenArchive(): React.JSX.Element {
  const { s, reload } = useData(fetchArchives)
  const d = s.kind === 'ok' && s.data.shop_id !== null ? s.data : null
  useScreenHead('Архив месяцев', d === null ? null : 'Excel с детализацией за месяц')
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  if (s.kind === 'loading') return <Loading label="Смотрю, какие месяцы собраны" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (d === null) return <NoShop reason={s.kind === 'ok' ? s.data.reason : undefined} />

  const download = async (month: string): Promise<void> => {
    setBusy(month + ':download'); setErr(null); setDone(null)
    const r = await apiFile(`/api/archives/${month}/file`)
    setBusy(null)
    if (!r.ok) { setErr(r.message); return }
    // Сохраняем через ссылку на объект: прямую ссылку не дать — запрос требует заголовков
    // авторизации, а <a href> их не отправляет.
    const url = URL.createObjectURL(r.blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Селлер-Штаб, ${monthTitle(month)}.xlsx`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
    setDone('Файл сохранён. Если на телефоне ничего не открылось — нажмите «Прислать в чат».')
  }

  const send = async (month: string): Promise<void> => {
    setBusy(month + ':send'); setErr(null); setDone(null)
    const r = await sendArchive(month)
    setBusy(null)
    if (!r.ok) { setErr(r.error.message); return }
    setDone('Файл отправлен вам в чат с ботом.')
  }

  const build = async (month: string): Promise<void> => {
    setBusy(month + ':build'); setErr(null); setDone(null)
    const r = await buildArchive(month)
    setBusy(null)
    if (!r.ok) { setErr(r.error.message); return }
    setDone(`${monthTitle(month)} собран.`)
    reload()
  }

  const card = (it: ArchiveItem): React.JSX.Element => (
    <div key={it.month} className="card card-pad gap-top" data-testid={`archive-${it.month}`}>
      <b>{monthTitle(it.month)}</b>
      <div className="hint gap-top-xs">
        Прибыль с учётом штрафов: {rub(it.totals['result_rub'])} · выручка {rub(it.totals['revenue'])}
      </div>
      <div className="hint">
        Собран {builtAt(it.built_at)} · {mb(it.size_bytes)}{it.in_cloud ? ' · копия в облаке' : ''}
      </div>
      {!it.complete && (
        <div className="hint calc-warn">Собран, когда отчёты WB пришли не за все дни — цифры могут измениться, стоит пересобрать.</div>
      )}
      <button type="button" className="btn btn-block gap-top-s" disabled={busy !== null}
              onClick={() => void send(it.month)}>
        {busy === it.month + ':send' ? 'Отправляю…' : 'Прислать в чат'}
      </button>
      <button type="button" className="btn btn-block gap-top-s" disabled={busy !== null}
              onClick={() => void download(it.month)}>
        {busy === it.month + ':download' ? 'Скачиваю…' : 'Скачать файл'}
      </button>
      <button type="button" className="link-inline gap-top-s" disabled={busy !== null}
              onClick={() => void build(it.month)}>
        {busy === it.month + ':build' ? 'Собираю заново…' : 'Пересобрать'}
      </button>
    </div>
  )

  return (
    <>
      {err !== null && <div className="card card-pad calc-warn gap-top">{err}</div>}
      {done !== null && <div className="card card-pad gap-top">{done}</div>}

      <Sect title="Готовые архивы" />
      {d.items.length === 0
        ? <p className="hint">Пока ни одного месяца. Архив соберётся сам, как только месяц будет закрыт отчётами WB.</p>
        : d.items.map(card)}

      {d.can_build.length > 0 && (
        <>
          <Sect title="Можно собрать" />
          <p className="hint">
            Эти месяцы закончились, а файла по ним ещё нет. Если месяц закрыт отчётами WB, архив соберётся
            сам в ближайший час — или соберите сейчас.
          </p>
          {d.can_build.map((m) => (
            <button key={m} type="button" className="btn btn-block gap-top-s" disabled={busy !== null}
                    data-testid={`archive-build-${m}`} onClick={() => void build(m)}>
              {busy === m + ':build' ? 'Собираю…' : `Собрать ${monthTitle(m)}`}
            </button>
          ))}
        </>
      )}

      <Sect title="Что внутри файла" />
      <p className="hint">
        Пять листов: сводка месяца с расходами построчно; экономика по каждой модели; по дням;
        детализация «день × модель»; и сырые строки отчётов WB о реализации — как их отдал сам WB.
        Открывается Excel, «Google Таблицами» и «Р7-Офис».
      </p>
    </>
  )
}
